import React, { useState, useEffect, useMemo } from "react";

import TestResultSection from "../sections/TestResultSection";
import ParameterSection from "../sections/ParameterSection";
import TestCaseSection from "../sections/TestCaseSection";
import QuestionSection from "../sections/QuestionSection";

import useTestSuiteSync from "../../../hooks/useTestSuiteSync";
import { useAutosave } from "../../../hooks/useAutosave";

import {
  fetchSuitesAndCases,
  runSelectedTestCases,
} from "../../../services/testRunnerAPI";

import {
  fetchStoredResults,
  fetchContractParameters,
  fetchDefaultContractParameters,
  saveContractParameters,
  fetchParamMetadata,
} from "../../../services/testResultsAPI";

import { useOrganisation } from "../../../contexts/OrganisationContext";
import { useContract } from "../../../contexts/ContractContext";
import { useAuth } from "../../../contexts/AuthContext";
import { IDSStepper, IDSStep, IDSAlert, IDSLink, IDSButton } from "@inera/ids-react";
import { fetchTestExecutions } from "../../../services/testExecutionAPI";
import ConfirmDialog from "../../ConfirmDialog";


/**
 * Main interface for running tests against a pre-selected service contract.
 * Expects activeContractId to already be set in ContractContext (via TestRun).
 * Uses a four-step stepper: parameters → test cases → results → questions/report.
 *
 * @param {string} mode - 'standard' (submit report) or 'sandbox' (isolated, no submission)
 */
const TestInterface = ({ mode = 'standard' }) => {
  const isSandbox = mode === 'sandbox';
  const isManual  = mode === 'manual';

  const [testSuites, setTestSuites] = useState([]);
  const { activeOrgId } = useOrganisation();
  const { activeContractId, activeContractName, activeContractVersion, activeContractDocUrl } = useContract();
  const { loading: authLoading, isAuthenticated } = useAuth();

  // Test execution state
  const [testResults, setTestResults] = useState([]);
  const [executionId, setExecutionId] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Key (`${suiteIndex}-${caseIndex}`) of the test case currently running
  // via its individual play button, or null when no single run is active.
  const [runningCaseKey, setRunningCaseKey] = useState(null);

  // Parameters state (shared between ParameterSection and TestCaseSection)
  const [parameters, setParameters] = useState(null);
  const [loadingParams, setLoadingParams] = useState(false);
  const [errorParams, setErrorParams] = useState(null);
  const [paramMetadata, setParamMetadata] = useState({});

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [justifications, setJustifications] = useState({});

  // Focus target for navigating from a status badge to its test result.
  // focusNonce increments on every navigation so repeat clicks re-trigger it.
  const [focusedResultKey, setFocusedResultKey] = useState(null);
  const [focusNonce, setFocusNonce] = useState(0);

  // Focus target for navigating from a test result back to its test case in
  // the TestCaseSection. Key is `${suiteIndex}-${caseIndex}`.
  const [focusedCaseKey, setFocusedCaseKey] = useState(null);
  const [caseFocusNonce, setCaseFocusNonce] = useState(0);

  // Status message state (replaces alert())
  const [statusMsg, setStatusMsg] = useState(null); // { type: 'success'|'error'|'warning', text: string }
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Incremented on every "Återställ vy" confirm to tell QuestionSection to
  // clear its own (unsaved) answers, since it fetches/holds them independently.
  const [resetSignal, setResetSignal] = useState(0);

  // Auto-dismiss the status message after a short delay.
  useEffect(() => {
    if (!statusMsg) return;
    const timer = setTimeout(() => setStatusMsg(null), 5000);
    return () => clearTimeout(timer);
  }, [statusMsg]);

  // Autosave parameters 10 s after the last change (writes to the sandbox-specific
  // configuration row when in sandbox mode, so it never touches the standard config)
  const paramAutosaveStatus = useAutosave(
    () => saveContractParameters(activeOrgId, activeContractId, parameters, isSandbox),
    [parameters],
    { delay: 10000, enabled: !!activeOrgId && !!activeContractId && parameters !== null }
  );

  // Use shared sync hook for managing checkbox state and collapse logic
  const {
    checkedSuites,
    checkedCases,
    contractChecked,
    collapsedSuites,
    setCollapsedSuites,
    setContractChecked,
    setCheckedCases,
    setCheckedSuites,
    syncContractChecked,
    syncSuiteChecked,
  } = useTestSuiteSync(testSuites);

  // Fetch test suites and existing execution when contract is ready
  useEffect(() => {
    if (!activeContractId || !activeContractName || authLoading || !isAuthenticated) return;

    let cancelled = false;

    setExecutionId(null);
    setExecutionStatus(null);
    setTestResults([]);

    fetchSuitesAndCases(activeContractName).then((data) => {
      if (cancelled) return;

      setTestSuites(data);
      setCollapsedSuites(Object.fromEntries(data.map((_, i) => [i, true])));
      setContractChecked(false);
      setCheckedSuites({});
      setCheckedCases({});

      // A manual self-declaration must create its own execution when submitted.
      // Reusing the latest automated or historical execution skips the declaration
      // creation endpoint, so its marked questions never become test results.
      if (isManual) {
        return;
      }

      fetchTestExecutions(activeOrgId, activeContractId, isSandbox).then((executions) => {
        if (cancelled) return;
        if (executions && executions.length > 0) {
          setExecutionId(executions[0].id);
          setExecutionStatus(executions[0].status ?? null);
        }
      });

      fetchStoredResults(activeOrgId, activeContractId, isSandbox).then((stored) => {
        if (cancelled) return;
        setTestResults(stored && stored.length > 0 ? stored : []);
      });
    });

    return () => { cancelled = true; };
  }, [activeContractId, activeContractName, activeOrgId, authLoading, isAuthenticated, isManual, isSandbox]);

  // Load parameters and param metadata when contract changes
  useEffect(() => {
    if (!activeContractId) return;
    const loadParams = async () => {
      setLoadingParams(true);
      try {
        const [params, meta] = await Promise.all([
          fetchContractParameters(activeOrgId, activeContractId, isSandbox),
          fetchParamMetadata(),
        ]);
        setParameters(params);
        setParamMetadata(meta);
      } catch (err) {
        setErrorParams(err.message);
      } finally {
        setLoadingParams(false);
      }
    };
    loadParams();
  }, [activeOrgId, activeContractId, isSandbox]);

  // Keep suite/contract checkbox state in sync when test cases change
  useEffect(() => {
    syncSuiteChecked();
    syncContractChecked();
  }, [checkedCases]);

  // Param change handlers
  const handleContractParamChange = (field, value) => {
    setParameters((prev) => {
      const updated = { ...prev };
      if (!updated.testsuite.contractParams)
        updated.testsuite.contractParams = {};
      updated.testsuite.contractParams[field] = value === "" ? null : value;
      return updated;
    });
  };

  const handleTestcaseParamChange = (testId, field, value) => {
    setParameters((prev) => {
      const updated = { ...prev };
      const test = updated.testsuite.testcaseParams?.find((t) => t["@id"] === testId);
      if (test) {
        if (!test.data) test.data = {};
        test.data[field] = value === "" ? null : value;
      }
      return updated;
    });
  };

  const handleSaveParams = async () => {
    try {
      await saveContractParameters(activeOrgId, activeContractId, parameters, isSandbox);
      setStatusMsg({ type: "success", text: "Parametrar sparade!" });
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatusMsg({ type: "error", text: `Kunde inte spara parametrar: ${err.message}` });
    }
  };

  const handleSaveAndRunTests = async () => {
    const webServiceUrl = parameters?.testsuite?.contractParams?.webServiceUrl;
    if (!webServiceUrl || webServiceUrl.trim() === "") {
      setStatusMsg({ type: "warning", text: "webServiceUrl måste anges innan tester kan köras." });
      return;
    }
    try {
      await saveContractParameters(activeOrgId, activeContractId, parameters, isSandbox);
      handleRunSelectedTestCases();
    } catch (err) {
      setStatusMsg({ type: "error", text: `Kunde inte spara parametrar: ${err.message}` });
    }
  };

  // Run a single test case via its individual play button. Saves parameters
  // first (matching the batch flow — sandbox saves go to the isolated sandbox
  // configuration), then runs only the selected case and merges the result
  // into testResults without discarding other results.
  const handleRunSingleTestCase = async (suiteIndex, caseIndex) => {
    if (!activeContractName) return;

    const webServiceUrl = parameters?.testsuite?.contractParams?.webServiceUrl;
    if (!webServiceUrl || webServiceUrl.trim() === "") {
      setStatusMsg({ type: "warning", text: "webServiceUrl måste anges innan tester kan köras." });
      return;
    }

    const suite = testSuites[suiteIndex];
    if (!suite) return;
    const testCaseName = suite.testCases[caseIndex];
    const caseKey = `${suiteIndex}-${caseIndex}`;

    const payload = {
      contractId: activeContractId,
      testSuites: [
        {
          testSuiteName: suite.testSuiteName,
          testCaseNames: [testCaseName],
          uncheckedTestCaseNames: suite.testCases.filter((_, i) => i !== caseIndex),
        },
      ],
      // Reuse the tracked execution (backend verifies it matches this sandbox mode)
      // so re-running a test case updates its result instead of creating a new one.
      ...(executionId && { executionId }),
    };

    try {
      setRunningCaseKey(caseKey);
      await saveContractParameters(activeOrgId, activeContractId, parameters, isSandbox);
      const results = await runSelectedTestCases(payload, activeOrgId, isSandbox);

      if (results && results.length > 0 && results[0].executionId) {
        setExecutionId(results[0].executionId);
      }

      // Merge the single-run result(s) into existing results, replacing the
      // matching entry by `${testSuite}-${testCase}` so other results remain.
      setTestResults((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const updated = [...prevArr];
        (results || []).forEach((res) => {
          const idx = updated.findIndex(
            (r) => r.testSuite === res.testSuite && r.testCase === res.testCase
          );
          if (idx >= 0) updated[idx] = res;
          else updated.push(res);
        });
        return updated;
      });
    } catch (err) {
      console.error("Error running test case:", err);
      setStatusMsg({ type: "error", text: err.message || "Något gick fel vid körning av testfallet." });
    } finally {
      setRunningCaseKey(null);
    }
  };

  // Run selected test cases via API
  const handleRunSelectedTestCases = async () => {    if (!activeContractId) return;

    const payload = {
      contractId: activeContractId,
      testSuites: [],
      // Reuse the tracked execution (backend verifies it matches this sandbox mode)
      // so re-running test cases updates their results instead of creating new ones.
      ...(executionId && { executionId })
    };

    testSuites.forEach((suite, index) => {
      const suiteCases = checkedCases[index];
      if (!suiteCases || !suiteCases.some(checked => checked)) return;

      const selectedCases = [];
      const unselectedCases = [];

      suiteCases.forEach((checked, i) => {
        if (checked) {
          selectedCases.push(suite.testCases[i]);
        } else {
          unselectedCases.push(suite.testCases[i]);
        }
      });

      payload.testSuites.push({
        testSuiteName: suite.testSuiteName,
        testCaseNames: selectedCases,
        uncheckedTestCaseNames: unselectedCases,
      });
    });

    if (payload.testSuites.length === 0) {
      setStatusMsg({ type: "warning", text: "Inga testfall valda." });
      return;
    }

    try {
      setIsLoading(true);
      const results = await runSelectedTestCases(payload, activeOrgId, isSandbox);

      if (results && results.length > 0 && results[0].executionId) {
        setExecutionId(results[0].executionId);
        setExecutionStatus(null);
      } else {
        try {
          const executions = await fetchTestExecutions(activeOrgId, activeContractId, isSandbox);
          if (executions && executions.length > 0) {
            setExecutionId(executions[0].id);
            setExecutionStatus(executions[0].status ?? null);
          }
        } catch (execErr) {
          console.error("Error fetching test executions:", execErr);
        }
      }

      setTestResults(results);
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error running tests:", err);
      setStatusMsg({ type: "error", text: err.message || "Något gick fel vid körning av testfall." });
    } finally {
      setIsLoading(false);
    }
  };

  // Total number of checked test cases across all suites (for the progress
  // indicator next to the batch run button).
  const selectedCount = Object.values(checkedCases).reduce(
    (sum, arr) => sum + (Array.isArray(arr) ? arr.filter(Boolean).length : 0),
    0
  );

  // Lookup of test result status keyed by `${testSuite}-${testCase}` so each
  // test case row in TestCaseSection can show its latest status badge.
  const resultStatusByKey = useMemo(() => {
    const map = {};
    (Array.isArray(testResults) ? testResults : []).forEach((r) => {
      map[`${r.testSuite}-${r.testCase}`] = r.status;
    });
    return map;
  }, [testResults]);

  const resultMessageByKey = useMemo(() => {
    const map = {};
    (Array.isArray(testResults) ? testResults : []).forEach((r) => {
      if (r && (r.status === 'FAILED' || r.status === 'NOT_EXECUTED')) {
        map[`${r.testSuite}-${r.testCase}`] = r.message;
      }
    });
    return map;
  }, [testResults]);

  const resultHasJustificationByKey = useMemo(() => {
    const map = {};
    (Array.isArray(testResults) ? testResults : []).forEach((r) => {
      if (r && r.status === 'FAILED') {
        map[`${r.testSuite}-${r.testCase}`] = !!(r.justification && r.justification.trim().length > 0);
      }
    });
    Object.entries(justifications || {}).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        map[key] = true;
      }
    });
    return map;
  }, [testResults, justifications]);

  // Check if all questions are answered
  const allQuestionsAnswered = () => {
    // TEXT_BLOCK has no answer; optional questions never block submission.
    // A mandatory question must be answered: TEXT needs text, YES_NO needs a choice.
    return Object.entries(questionAnswers).every(([, ans]) => {
      if (ans.questionType === 'TEXT_BLOCK') return true;
      if (ans.mandatory === false) return true;
      if (ans.questionType === 'TEXT' || ans.questionType === 'DATE') return (ans.textValue || '').trim() !== '';
      return ans.answer !== null;
    });
  };

  const allFailedTestsJustified = () => {
    if (!Array.isArray(testResults)) return true;
    return testResults
      .filter(test => test.status === "FAILED" || test.status === "NOT_EXECUTED")
      .every(test => {
        const key = `${test.testSuite}-${test.testCase}`;
        return justifications[key]?.trim().length > 0;
      });
  };

  // Resets the view to a blank starting state and restores the contract's
  // default parameter payload instead of leaving any previously edited values
  // in the form.
  const handleResetView = async () => {
    setCurrentStep(0);
    setTestResults([]);
    setExecutionId(null);
    setExecutionStatus(null);
    setQuestionAnswers({});
    setJustifications({});
    setContractChecked(false);
    setCheckedSuites({});
    setCheckedCases({});
    setCollapsedSuites(Object.fromEntries(testSuites.map((_, i) => [i, true])));
    setFocusedResultKey(null);
    setFocusedCaseKey(null);
    setStatusMsg(null);
    setResetSignal((n) => n + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (!activeContractId) return;

    try {
      const defaults = await fetchDefaultContractParameters(activeContractId);
      setParameters(defaults);
      setErrorParams(null);
    } catch (err) {
      setErrorParams(err.message);
      setParameters({
        testsuite: { contractParams: {}, testcaseParams: [] },
      });
    }
  };

  const handleCurrentStep = (step) => {
    if (step !== currentStep) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Navigate from a test case status badge to that case's result. Opens the
  // results step and signals TestResultSection which case to expand/scroll to.
  const handleNavigateToResult = (testSuite, testCase) => {
    setFocusedResultKey(`${testSuite}-${testCase}`);
    setFocusNonce((n) => n + 1);
    setCurrentStep(2);
  };

  // Navigate from a test result back to its test case in the selection step.
  // Resolves suite/case names to indices and signals TestCaseSection to
  // expand and scroll to that case.
  const handleNavigateToTestCase = (testSuite, testCase) => {
    const suiteIndex = testSuites.findIndex((s) => s.testSuiteName === testSuite);
    if (suiteIndex === -1) return;
    const caseIndex = testSuites[suiteIndex].testCases.indexOf(testCase);
    if (caseIndex === -1) return;
    setFocusedCaseKey(`${suiteIndex}-${caseIndex}`);
    setCaseFocusNonce((n) => n + 1);
    setCurrentStep(1);
  };

  return (
    <div className="ids-p-6" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {activeContractName && (
        <div style={{ marginTop: 0, marginBottom: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <h1 className="ids-heading-l" style={{ margin: 0 }}>
              {activeContractName}
              {activeContractVersion ? ` ${activeContractVersion}` : ""}
            </h1>
            {activeContractDocUrl && (
              <p style={{ margin: "0.4rem 0 0", fontSize: "0.9rem" }}>
       <IDSLink underlined>
         <a href={activeContractDocUrl} target="_blank" rel="noopener noreferrer">
           <span>
             Visa dokumentation för tjänstekontraktet
             <span aria-hidden="true" className="ids-icon-external-link-small ids-icon--text-end" />
           </span>
         </a>
       </IDSLink>
              </p>
            )}
          </div>

          <IDSButton
            size="m"
            secondary
            type="button"
            onClick={() => setShowResetConfirm(true)}
            aria-label="Börja om"
          >
            Börja om
          </IDSButton>
        </div>
      )}
      {statusMsg && (
        <div className="ids-mb-4">
          <IDSAlert type={statusMsg.type === "warning" ? "attention" : statusMsg.type}>
            <p>{statusMsg.text}</p>
            <button
              type="button"
              style={{ position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
              onClick={() => setStatusMsg(null)}
              aria-label="Stäng"
            >
              ×
            </button>
          </IDSAlert>
        </div>
      )}

      {/* Sandbox info banner */}
      {isSandbox && (
        <div className="ids-mb-4">
          <IDSAlert type="attention">
            <p>
              <strong>Labbmiljö</strong> – Testerna körs isolerat och påverkar inte befintliga rapporter.
              Inga resultat kan skickas in för granskning i detta läge.
            </p>
          </IDSAlert>
        </div>
      )}

      {/* Manual mode info banner */}
      {isManual && (
        <div className="ids-mb-4">
          <IDSAlert type="info">
            <p>
              <strong>Självdeklaration</strong> – Besvara alla avsnitt nedan för att dokumentera
              hur tjänsteproducenten uppfyller kraven. Skicka sedan in underlaget för granskning.
            </p>
          </IDSAlert>
        </div>
      )}

      {/* Standard mode: show review warning if a report is already in review */}
      {!isSandbox && !isManual && (executionStatus === 'PENDING_REVIEW' || executionStatus === 'IN_REVIEW') && (
        <div className="ids-mb-4">
          <IDSAlert type="attention">
            <p>
              En testrapport är inskickad och inväntar granskning. Du kan fortfarande köra tester,
              men det går inte att skicka in en ny rapport förrän den pågående granskningen är avslutad.
            </p>
          </IDSAlert>
        </div>
      )}

      {/* ── Manual mode: full self-declaration form (no automated test run) ── */}
      {isManual ? (
        <QuestionSection
          contractId={activeContractId}
          organisationId={activeOrgId}
          executionId={executionId}
          executionStatus={executionStatus}
          onAnswersChange={setQuestionAnswers}
          allQuestionsAnswered={allQuestionsAnswered()}
          allFailedTestsJustified={true}
          testResults={[]}
          justifications={{}}
          scenario="local"
          resetSignal={resetSignal}
        />
      ) : (
      /* ── Standard / Sandbox stepper (3–4 steps) ── */
      <IDSStepper>
        {/* Step 1: Contract Parameters */}
        <IDSStep
          headline="Kontraktsparametrar"
          expanded={currentStep === 0}
          onClick={() => handleCurrentStep(0)}
        >
          <p className="ids-mb-4">
            Fyll i de parametrar som tjänstekontraktet behöver, till exempel webbtjänstens
            URL. Dessa värden används som standard för samtliga testfall – vissa testfall kan
            senare skriva över enskilda värden för sitt eget behov. Spara parametrarna för att
            gå vidare till att välja testfall.
          </p>
          <ParameterSection
            selectedContract={activeContractName}
            parameters={parameters}
            loadingParams={loadingParams}
            errorParams={errorParams}
            onContractParamChange={handleContractParamChange}
            onSaveParams={handleSaveParams}
            autosaveStatus={paramAutosaveStatus}
            paramMetadata={paramMetadata}
          />
        </IDSStep>

        {/* Step 2: Select and Run Tests */}
        <IDSStep
          headline="Välj tester och kör"
          expanded={currentStep === 1}
          onClick={() => handleCurrentStep(1)}
        >
          <p className="ids-mb-4">
            Markera de testsviter och testfall du vill köra. Du kan köra alla valda
            testfall samtidigt eller starta ett enskilt testfall. Vissa
            testfall har egna parameterfält – fyll bara i dessa om just det testfallet ska
            avvika från kontraktsparametrarna, annars används kontraktets värden automatiskt.
          </p>
          {parameters ? (
            <TestCaseSection
              selectedContract={activeContractName}
              testSuites={testSuites}
              contractChecked={contractChecked}
              setContractChecked={setContractChecked}
              checkedSuites={checkedSuites}
              setCheckedSuites={setCheckedSuites}
              checkedCases={checkedCases}
              setCheckedCases={setCheckedCases}
              collapsedSuites={collapsedSuites}
              setCollapsedSuites={setCollapsedSuites}
              isLoading={isLoading}
              onSaveAndRunTests={handleSaveAndRunTests}
              onRunSingleTestCase={handleRunSingleTestCase}
              runningCaseKey={runningCaseKey}
              selectedCount={selectedCount}
              resultStatusByKey={resultStatusByKey}
              resultMessageByKey={resultMessageByKey}
              resultHasJustificationByKey={resultHasJustificationByKey}
              onNavigateToResult={handleNavigateToResult}
              focusKey={focusedCaseKey}
              focusNonce={caseFocusNonce}
              testcaseParams={parameters?.testsuite?.testcaseParams || []}
              onTestcaseParamChange={handleTestcaseParamChange}
              paramMetadata={paramMetadata}
            />
          ) : (
            <p className="ids-mt-4">Spara kontraktsparametrar först.</p>
          )}
        </IDSStep>

        {/* Step 3: Test Results */}
        <IDSStep
          headline="Granska testresultat"
          expanded={currentStep === 2}
          onClick={() => handleCurrentStep(2)}
        >
          <p className="ids-mb-4">
            Här ser du resultatet av de körda testfallen.{isSandbox ? '' : ' Testfall som inte godkänts måste motiveras innan du kan gå vidare till att besvara frågor.'}
          </p>
          {testResults.length > 0 ? (
            <TestResultSection
              testStatuses={testResults}
              onJustificationsChange={setJustifications}
              onNext={isSandbox ? null : () => { setCurrentStep(3); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              focusKey={focusedResultKey}
              focusNonce={focusNonce}
              onNavigateToTestCase={handleNavigateToTestCase}
              isSandbox={isSandbox}
            />
          ) : (
            <p className="ids-mt-4">Inga testresultat tillgängliga. Kör tester först.</p>
          )}
        </IDSStep>

        {/* Step 4: Questions — hidden in sandbox mode */}
        {!isSandbox && (
          <IDSStep
            headline="Besvara frågor"
            expanded={currentStep === 3}
            onClick={() => handleCurrentStep(3)}
          >
            <p className="ids-mb-4">
              Besvara de kompletterande frågorna om hur tjänsteproducenten uppfyller kraven.
            </p>
            <QuestionSection
              contractId={activeContractId}
              organisationId={activeOrgId}
              executionId={executionId}
              executionStatus={executionStatus}
              onAnswersChange={setQuestionAnswers}
              allQuestionsAnswered={allQuestionsAnswered()}
              allFailedTestsJustified={allFailedTestsJustified()}
              testResults={testResults}
              justifications={justifications}
              resetSignal={resetSignal}
            />
          </IDSStep>
        )}
      </IDSStepper>
      )} {/* end isManual ternary */}

      <ConfirmDialog
        isOpen={showResetConfirm}
        title="Börja om?"
        message="Detta rensar rapporten och återställer flödet så att du kan börja från början."
        confirmLabel="Ja, börja om"
        cancelLabel="Avbryt"
        onCancel={() => setShowResetConfirm(false)}
        onConfirm={() => {
          setShowResetConfirm(false);
          handleResetView();
        }}
      />
    </div>
  );
};

export default TestInterface;
